import { gql } from '@apollo/client';
import { snakeCase, camelCase } from 'lodash';
import { dateFormFormat } from '@/components/utils/formatter';
import type { FileType } from '@/types';
import { Schema, defaultValues } from './schema';

export const REGULAR_HEALTH_CHECKUP_RESULT = gql`
  query regularHealthCheckupResult($personalId: PersonalId!, $resultId: ID!) {
    client {
      id
      regularHealthCheckupResult(personalId: $personalId, resultId: $resultId) {
        id
        office {
          id
        }
        healthCheckupInstitution {
          id
        }
        dateOn
        orderByDoctor
        report
        specifiedWorkCode
        regularHealthCheckupResultItems {
          id
          itemCode
          checked
          value
          evaluation
          hasFinding
        }
      }
    }
  }
`;

export interface RegularHealthCheckupResultQueryVariables {
  personalId: string;
  resultId: string;
}

export interface RegularHealthCheckupResultQueryResult {
  client: {
    regularHealthCheckupResult: RegularHealthCheckupResult | null;
  };
}

export interface RegularHealthCheckupResult {
  id: string;
  office?: Office;
  healthCheckupInstitution?: HealthCheckupInstitution;
  dateOn: string;
  orderByDoctor: boolean;
  report?: FileType;
  specifiedWorkCode?: string;
  regularHealthCheckupResultItems: RegularHealthCheckupResultItem[];
}

interface Office {
  id: string;
  name: string;
}

interface HealthCheckupInstitution {
  id: string;
  name: string;
  location: string;
}

export interface RegularHealthCheckupResultItem {
  id: string;
  itemCode: string;
  checked: boolean;
  hasFinding: boolean;
  value?: string;
  evaluation?: string;
}

export const generateDefaultValues = (data: RegularHealthCheckupResult | null | undefined): Schema => {
  if (!data) {
    return defaultValues;
  }

  const values: Schema = {
    dateOn: dateFormFormat(data.dateOn, 'L'),
    orderByDoctor: data.orderByDoctor ? 'true' : 'false',
    report: data.report ?? null,
    officeId: data.office?.id,
    healthCheckupInstitutionId: data.healthCheckupInstitution?.id,
    specifiedWorkCode: data.specifiedWorkCode ? [data.specifiedWorkCode] : [],
    items: { ...defaultValues.items }
  };

  for (const item of data.regularHealthCheckupResultItems) {
    values.items[camelCase(item.itemCode)] = {
      checked: String(item.checked),
      value: item.value,
      evaluation: item.evaluation,
      hasFinding: String(item.hasFinding)
    };
  }

  return values;
};

export const UPDATE_REGULAR_HEALTH_CHECKUP_RESULT = gql`
  mutation updateRegularHealthCheckupResult($input: UpdateRegularHealthCheckupResultInput!) {
    updateRegularHealthCheckupResult(input: $input) {
      regularHealthCheckupResult {
        id
      }
    }
  }
`;

export interface UpdateRegularHealthCheckupResultQueryVariables {
  input: {
    personalId: string;
    resultId: string;
    attributes: {
      officeId?: string | null;
      healthCheckupInstitutionId?: string | null;
      dateOn: string;
      orderByDoctor?: boolean | null;
      report?: File | FileType | null;
      specifiedWorkCode?: string | null;
      items: {
        itemCode: string;
        checked?: boolean | null;
        value?: string | null;
        evaluation?: string | null;
        hasFinding?: boolean | null;
      }[];
    };
  };
}

export interface UpdateRegularHealthCheckupResultQueryResult {
  updateRegularHealthCheckupResult: {
    regularHealthCheckupResult: {
      id: string;
    };
  };
}

export const convertFormDataToQueryVariables = (
  data: Schema,
  personalId: string,
  resultId: string
): UpdateRegularHealthCheckupResultQueryVariables => {
  return {
    input: {
      personalId,
      resultId,
      attributes: {
        officeId: data.officeId,
        healthCheckupInstitutionId: data.healthCheckupInstitutionId,
        dateOn: data.dateOn,
        orderByDoctor: data.orderByDoctor === 'true',
        report: data.report,
        specifiedWorkCode: data.specifiedWorkCode[0] || null,
        items: Object.entries(data.items).map(([itemCode, item]) => ({
          itemCode: snakeCase(itemCode).replace('_hz', 'hz'),
          checked: item.checked === 'true',
          value: item.value,
          evaluation: item.evaluation,
          hasFinding: item.hasFinding === 'true'
        }))
      }
    }
  };
};
